import { migrateLegacy } from './migrate.js';

export const SCHEMA_VERSION = 2;
export const EMPTY_STATE = { players: [], games: [] };
export const LOCAL_STORAGE_KEY = 'mlb-show-tracker';
export const backupKey = (key) => `${key}.backup-v1`;

export function toCount(value, fallback = 0) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

const list = (value) => (Array.isArray(value) ? value : []);

function hasContent(raw) {
  return list(raw?.players).length > 0 || list(raw?.games).length > 0;
}

export function needsMigration(raw) {
  return Boolean(raw) && raw.version !== SCHEMA_VERSION && hasContent(raw);
}

function cleanCurrent(raw) {
  const players = list(raw.players)
    .filter((player) => player && player.id != null && String(player.name || '').trim())
    .map((player) => ({ id: String(player.id), name: String(player.name).trim() }));
  const known = new Set(players.map((player) => player.id));
  const games = list(raw.games)
    .filter((game) => game && game.id != null && known.has(String(game.home)) && known.has(String(game.away)))
    .map((game) => ({
      id: String(game.id),
      createdAt: Number.isFinite(Number(game.createdAt)) ? Number(game.createdAt) : 0,
      home: String(game.home),
      away: String(game.away),
      homeScore: toCount(game.homeScore),
      awayScore: toCount(game.awayScore),
      innings: toCount(game.innings, 9) || 9,
      date: typeof game.date === 'string' ? game.date : '',
    }))
    .filter((game) => game.homeScore !== game.awayScore);
  return { players, games };
}

/** Any stored shape → { state, report }. report is set only when legacy data was converted. */
export function convertState(raw) {
  if (!raw || !hasContent(raw)) return { state: { ...EMPTY_STATE }, report: null };
  if (raw.version === SCHEMA_VERSION) return { state: cleanCurrent(raw), report: null };
  const { players, games, report } = migrateLegacy(list(raw.players), list(raw.games));
  return { state: cleanCurrent({ players, games }), report };
}

export const serializeState = (state) => ({ version: SCHEMA_VERSION, ...cleanCurrent(state) });

export function isEmptyState(state) {
  return state.players.length === 0 && state.games.length === 0;
}

export function logMigration(where, report) {
  if (report && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info(`[mlb-the-show] migrated ${where} tracker to v${SCHEMA_VERSION}`, report);
  }
}

export function createLocalStorageAdapter(key = LOCAL_STORAGE_KEY, store = window.localStorage) {
  const write = (state) => store.setItem(key, JSON.stringify(serializeState(state)));
  return {
    async load() {
      try {
        const text = store.getItem(key);
        const raw = text ? JSON.parse(text) : null;
        const { state, report } = convertState(raw);
        if (needsMigration(raw)) {
          if (store.getItem(backupKey(key)) == null) store.setItem(backupKey(key), text);
          write(state);
          logMigration('browser', report);
        }
        return state;
      } catch {
        return { ...EMPTY_STATE };
      }
    },
    async save(data) {
      try {
        write(data);
      } catch {
        /* private mode or quota — keep the in-memory tracker usable */
      }
    },
  };
}
